/**
 * BudgetPacingCard — Daily/Monthly budget pacing for TJ Command Center Phase 1.5B
 *
 * Calculates pacing from campaign daily_budget sums vs today's/month's spend.
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAdCampaigns, type AdPerformanceSnapshot } from "@/hooks/useAdPerformance";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface PacingData {
  dailyBudget: number;
  spentToday: number;
  dailyPct: number;
  monthlyBudget: number;
  spentThisMonth: number;
  monthlyPct: number;
  projectedMonthly: number;
  projectedPct: number;
}

function PaceIndicator({ projected, budget }: { projected: number; budget: number }) {
  if (budget <= 0) return null;
  const diff = ((projected - budget) / budget) * 100;
  if (Math.abs(diff) < 5) return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  if (diff > 0)
    return (
      <span className="flex items-center gap-0.5 text-xs text-yellow-600">
        <TrendingUp className="h-3.5 w-3.5" />
        +{diff.toFixed(1)}%
      </span>
    );
  return (
    <span className="flex items-center gap-0.5 text-xs text-green-600">
      <TrendingDown className="h-3.5 w-3.5" />
      {diff.toFixed(1)}%
    </span>
  );
}

export function BudgetPacingCard({
  snapshots,
}: {
  snapshots: AdPerformanceSnapshot[];
}) {
  const campaigns = useAdCampaigns();

  const pacing = useMemo<PacingData | null>(() => {
    const activeCampaigns = (campaigns.data ?? []).filter(
      (c) => c.status === "ACTIVE" && c.daily_budget && c.daily_budget > 0
    );
    if (activeCampaigns.length === 0) return null;

    const dailyBudget = activeCampaigns.reduce((sum, c) => sum + (c.daily_budget ?? 0), 0);

    const today = new Date().toISOString().slice(0, 10);
    const monthStart = today.slice(0, 7); // "YYYY-MM"

    let spentToday = 0;
    let spentThisMonth = 0;

    for (const snap of snapshots) {
      if (snap.date === today) spentToday += snap.spend || 0;
      if (snap.date.startsWith(monthStart)) {
        spentThisMonth += snap.spend || 0;
      }
    }

    const dayOfMonth = new Date().getDate();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const monthlyBudget = dailyBudget * daysInMonth;

    // Project based on true avg daily spend (including zero-spend days like weekends)
    const avgDailySpend = dayOfMonth > 0 ? spentThisMonth / dayOfMonth : spentToday;
    const remainingDays = daysInMonth - dayOfMonth;
    const projectedMonthly = spentThisMonth + avgDailySpend * remainingDays;

    return {
      dailyBudget,
      spentToday,
      dailyPct: dailyBudget > 0 ? Math.min(100, (spentToday / dailyBudget) * 100) : 0,
      monthlyBudget,
      spentThisMonth,
      monthlyPct: monthlyBudget > 0 ? Math.min(100, (spentThisMonth / monthlyBudget) * 100) : 0,
      projectedMonthly,
      projectedPct: monthlyBudget > 0 ? (projectedMonthly / monthlyBudget) * 100 : 0,
    };
  }, [snapshots, campaigns.data]);

  if (!pacing) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Budget Pacing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Daily */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Daily: {formatCurrency(pacing.spentToday)} / {formatCurrency(pacing.dailyBudget)}
            </span>
            <span className="font-medium">{pacing.dailyPct.toFixed(0)}%</span>
          </div>
          <Progress value={pacing.dailyPct} className="h-2" />
        </div>

        {/* Monthly */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Monthly: {formatCurrency(pacing.spentThisMonth)} / {formatCurrency(pacing.monthlyBudget)}
            </span>
            <span className="font-medium">{pacing.monthlyPct.toFixed(0)}%</span>
          </div>
          <Progress value={pacing.monthlyPct} className="h-2" />
        </div>

        {/* Projected */}
        <div className="flex items-center justify-between text-xs border-t pt-2">
          <span className="text-muted-foreground">
            Projected: {formatCurrency(pacing.projectedMonthly)}
          </span>
          <PaceIndicator projected={pacing.projectedMonthly} budget={pacing.monthlyBudget} />
        </div>
      </CardContent>
    </Card>
  );
}
